/**
 * rc-sidecart-bridge.js
 * ---------------------------------------------------------------------------
 * Bridges Horizon-native add-to-cart surfaces (home, product.json PDP,
 * collection quick-add, Horizon cart upsells/GWP) to the LEGACY sidecart
 * section (`sections/cart-drawer.liquid`), so the legacy drawer opens after an
 * add exactly the way the old theme does internally.
 *
 * How the legacy theme opens the drawer (theme.js, repeated at 4 call sites,
 * e.g. product-form addSuccess / GWP / bundle):
 *
 *     if (document.querySelector('cart-drawer[data-drawer="on"]') !== null) {
 *       document.querySelector('cart-main').refresh()
 *       document.querySelector('cart-drawer').open()
 *     }
 *
 * This bridge reuses those exact canonical entry points (`cart-main.refresh()`
 * + `cart-drawer.open()`) rather than reimplementing drawer logic.
 *
 * Horizon announces a successful add via the `cart:update` event
 * (ThemeEvents.cartUpdate — dispatched by CartAddEvent / CartUpdateEvent in
 * assets/events.js, bubbles to `document`; `detail.data.didError === true`
 * marks failures). We listen for that, and additionally intercept fetch/XHR to
 * `/cart/add` as a fallback for any add path that does not emit the event.
 *
 * Idempotency:
 *  - On LEGACY-zone pages the old theme.js already runs its own form handlers
 *    and opens the drawer itself; those legacy adds do NOT dispatch
 *    `cart:update`, so the event listener never double-fires for them. The
 *    fetch/XHR fallback and the event path both funnel through one guarded,
 *    debounced `openSidecart()` so a stray double-trigger is a harmless no-op
 *    (re-adding the `in` class / re-refreshing is idempotent).
 *  - Suppressed entirely on the cart template (the legacy drawer is not
 *    rendered there; we also guard in JS).
 *  - cart_type stays `page`; Horizon's own `cart-drawer-component` lacks the
 *    `auto-open` attribute and is not rendered under the legacy chrome, so it
 *    never competes.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Coalesce window: an add can surface as BOTH a `cart:update` event and an
  // intercepted /cart/add request. Collapse those into a single open within
  // this window so we never refresh/open twice for one logical add.
  var COALESCE_MS = 250;
  var lastOpenAt = 0;

  /**
   * The legacy drawer is intentionally NOT rendered on the cart template
   * (see snippets/rc-sidecart.liquid). This is a belt-and-suspenders JS guard
   * for the case where the markup is present but should stay inert.
   */
  function isCartTemplate() {
    // The snippet already skips rendering the drawer on the cart template via
    // Liquid (`unless template.name == 'cart'`). This is the matching JS guard
    // for client-side navigations; it reads <main data-template> (since D-085
    // <body> also carries production's template-* classes).
    var main = document.querySelector('main[data-template]');
    if (!main) return false;
    var tpl = main.getAttribute('data-template') || '';
    return tpl === 'cart' || tpl.indexOf('cart') === 0;
  }

  /**
   * Reuse the legacy theme's canonical open/refresh entry points.
   * Mirrors theme.js: refresh() then open(), gated on a drawer that is
   * actually in "drawer" mode (data-drawer="on").
   */
  function openSidecart() {
    if (isCartTemplate()) {
      console.log('[rc-sidecart-bridge] suppressed on cart template');
      return;
    }

    var drawer = document.querySelector('cart-drawer[data-drawer="on"]');
    if (!drawer) {
      // No legacy drawer on this page (or not in drawer mode) — nothing to do.
      // Do NOT navigate; native fallbacks already handle their own UX.
      return;
    }

    var now = Date.now();
    if (now - lastOpenAt < COALESCE_MS) {
      // Already handled this add a moment ago (event + fetch arriving together).
      return;
    }
    lastOpenAt = now;

    var main = document.querySelector('cart-main');
    if (main && typeof main.refresh === 'function') {
      main.refresh();
    }
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else if (!drawer.classList.contains('in')) {
      // Fallback if the custom element has not upgraded yet.
      drawer.classList.add('in');
    }
  }

  // ---- Header badge (QA DEF-025, D-058) ----
  // The legacy header badge (.cartCount, snippets/nav-contents.liquid) was only ever updated as a side
  // effect of the legacy drawer's refresh(), which is not rendered on the Horizon cart page, and refresh()
  // never un-hides it. Keep it on the server's count after every cart change, on every page: the cart the
  // event carries when it has one, else /cart.js. Never detail.data.itemCount, which for product-form adds
  // is the quantity just added, not the total.
  var badgeTicket = 0;
  var reconcileTimer = 0;

  function writeBadge(count) {
    var badges = document.querySelectorAll('.cartCount');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (!badge) continue;
      if (!badge.querySelector('.visually-hidden')) {
        // D-070: the legacy drawer's refresh() rewrites badges with .html(count), dropping the label; put it back.
        var label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = 'Cart items: ';
        badge.insertBefore(label, badge.firstChild);
      }
      // Replace only the number: the server render puts a visually hidden "Cart items:" label before it.
      var last = badge.lastChild;
      if (last && last.nodeType === 3) {
        last.nodeValue = String(count);
      } else {
        badge.appendChild(document.createTextNode(String(count)));
      }
      badge.classList.toggle('hidden-count', count === 0);
    }
  }

  function syncBadge(cart) {
    if (cart && typeof cart.item_count === 'number') {
      badgeTicket++; // supersedes any /cart.js request still in flight
      writeBadge(cart.item_count);
      // An event's cart can arrive after a newer one (the GWP block dispatches after a section render), so
      // re-read the server shortly after and converge on it.
      clearTimeout(reconcileTimer);
      reconcileTimer = setTimeout(function () {
        syncBadge(null);
      }, 300);
      return;
    }
    var ticket = ++badgeTicket;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window
      .fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (json) {
        // Only the latest request writes, so rapid changes can't leave an older count behind.
        if (ticket === badgeTicket && json && typeof json.item_count === 'number') writeBadge(json.item_count);
      })
      .catch(function () {
        // Network failure: leave the badge as it is.
      });
  }

  // Back/forward cache restores the page as it was; re-read the cart.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) syncBadge(null);
  });

  // D-062: a Back or Forward without the cache reloads the page, often from the HTTP cache, whose badge was
  // rendered before the last cart change (QA DEF-031). Re-read the cart on those navigations as well.
  try {
    var navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0]
    );
    if (navigation && navigation.type === 'back_forward') syncBadge(null);
  } catch (e) {
    // Navigation Timing unavailable: the badge keeps the rendered count.
  }

  // D-070: a change made inside the legacy drawer (its Remove button, its stepper) ends in cart-main.refresh(),
  // which swaps cart-main's children and then writes every badge with .html(count): no label, never hidden at 0.
  // This observer's callback runs after that write, so re-reading /cart.js here gives the bridge the last word.
  var watchedDrawer = /** @type {Element | null} */ (null);
  function watchDrawer() {
    var main = document.querySelector('cart-main');
    if (!main || main === watchedDrawer) return;
    watchedDrawer = main;
    new MutationObserver(function () {
      // Put the label and the hidden-at-0 state back at once, from the count refresh() just wrote (a /cart.js
      // read can be slow or fail), then confirm the count from the server.
      var badge = document.querySelector('.cartCount');
      var shown = badge ? parseInt((badge.textContent || '').replace(/\D+/g, ''), 10) : NaN;
      if (!isNaN(shown)) writeBadge(shown);
      syncBadge(null);
    }).observe(main, { childList: true });
  }
  watchDrawer();
  document.addEventListener('DOMContentLoaded', watchDrawer);

  // ---- Path 1: Horizon cart:update event (CartAddEvent / CartUpdateEvent) ----
  // Fires for product-form-component, quick-add (product-form in modal),
  // component-cart-items, quick-order-list, rc-cart-upsell, rc-cart-gwp.
  document.addEventListener('cart:update', function (event) {
    var detail = (event && event.detail) || {};
    var data = detail.data || {};
    // Every cart change updates the badge, errors included: a 422 over the maximum still adds the allowed
    // quantity. D-058.
    syncBadge(detail.resource);
    if (data.didError) {
      // Failed add — never open on error.
      return;
    }
    openSidecart();
  });

  // ---- Path 2: fetch() fallback for any add path to /cart/add ----
  // Belt-and-suspenders for add paths that do not emit `cart:update`
  // (e.g. third-party widgets). Coalesced with Path 1 via openSidecart().
  if (window.fetch && !window.__rcSidecartFetchPatched) {
    window.__rcSidecartFetchPatched = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = (typeof input === 'string') ? input : (input && input.url) || '';
      } catch (e) {
        url = '';
      }
      var isAdd = url.indexOf('/cart/add') !== -1;
      var promise = nativeFetch.apply(this, arguments);
      if (isAdd) {
        promise.then(function (response) {
          // D-058: any successful answer re-reads the badge, whatever its content type: Shopify serves
          // /cart/add.js as text/javascript, and app adds on the cart page (no drawer there) need it too.
          if (response && response.ok) syncBadge(null);
          // Only open on a real success (2xx + JSON). Cloudflare/error HTML or
          // 4xx/5xx must not pop the drawer. Shopify's .js endpoints answer text/javascript, so this path
          // opens nothing for them; Path 1 and theme.js open the drawer for their own adds.
          try {
            if (!response || !response.ok) return response;
            var ct = (response.headers && response.headers.get('content-type')) || '';
            if (ct.indexOf('application/json') === -1) return response;
          } catch (e) {
            return response;
          }
          openSidecart();
          return response;
        }).catch(function () {
          // Swallow — the original consumer handles its own errors. A lost response may still have added
          // the item, so re-read the badge (D-058).
          syncBadge(null);
        });
      }
      return promise;
    };
  }

  // ---- Path 3: XHR fallback for legacy/jQuery $.ajax add paths ----
  // theme.js (rc-legacy.js) submits adds via jQuery $.ajax (XHR), and it opens
  // the drawer itself on legacy-zone pages. This path opens the drawer only for
  // an add endpoint that answers application/json; Shopify's /cart/add.js answers
  // text/javascript, so legacy adds are left to theme.js (the COALESCE guard only
  // tracks the bridge's own opens). The badge re-read runs on any 2xx (D-058).
  if (window.XMLHttpRequest && !window.__rcSidecartXhrPatched) {
    window.__rcSidecartXhrPatched = true;
    var XHR = window.XMLHttpRequest;
    var open = XHR.prototype.open;
    XHR.prototype.open = function (method, url) {
      try {
        this.__rcIsCartAdd = typeof url === 'string' && url.indexOf('/cart/add') !== -1;
      } catch (e) {
        this.__rcIsCartAdd = false;
      }
      return open.apply(this, arguments);
    };
    var send = XHR.prototype.send;
    XHR.prototype.send = function () {
      if (this.__rcIsCartAdd) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) syncBadge(null); // D-058, whatever the content type
          // Drawer: success only, 2xx and a JSON content-type.
          try {
            if (this.status < 200 || this.status >= 300) return;
            var ct = this.getResponseHeader && this.getResponseHeader('content-type');
            if (!ct || ct.indexOf('application/json') === -1) return;
          } catch (e) {
            return;
          }
          openSidecart();
        });
      }
      return send.apply(this, arguments);
    };
  }
})();
